import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { signInWithPassword } from '../lib/supabaseAuth';
import { supabase } from '../lib/supabaseClient';
import { MEMBER_PASSWORD_RULE_LABEL, validateEmail, validatePassword, validateZipcode } from '../utils/memberUtils';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { AUTH_LIGHT_SCREEN_BG, useBodyBackgroundOnMount } from '../hooks/useBodyBackgroundOnMount';
import { requiresPasswordSetup } from '../lib/passwordSetup';
import { isPersonalLoginEnabled } from '../lib/personalLoginAccess';

export default function MemberLogin() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [registerStage, setRegisterStage] = useState<'auth' | 'clinic' | 'profile' | 'done'>('auth');
  const [completedPin, setCompletedPin] = useState('');
  const [completedClinicId, setCompletedClinicId] = useState('');
  
  // ログインフォーム
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // 登録フォーム
  const [registerInitialStaffName, setRegisterInitialStaffName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [registerClinicName, setRegisterClinicName] = useState('');
  const [registerZipcode, setRegisterZipcode] = useState('');
  const [registerPrefecture, setRegisterPrefecture] = useState('');
  const [registerCity, setRegisterCity] = useState('');
  const [registerTownStreet, setRegisterTownStreet] = useState('');
  const [registerBuildingName, setRegisterBuildingName] = useState('');
  const [registerClinicPhone, setRegisterClinicPhone] = useState('');
  const [pendingRegistrationEmail, setPendingRegistrationEmail] = useState<string | null>(null);
  const [resendAvailableAtMs, setResendAvailableAtMs] = useState<number | null>(null);
  const [resendNowMs, setResendNowMs] = useState(() => Date.now());

  // インラインメッセージ
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  // UI helpers
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);
  const [registerPasswordHasBlurred, setRegisterPasswordHasBlurred] = useState(false);
  const [registerConfirmPasswordHasBlurred, setRegisterConfirmPasswordHasBlurred] = useState(false);

  // Zipcode -> Address autofill
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const lastZipLookupRef = useRef<string>('');
  const zipAbortRef = useRef<AbortController | null>(null);
  const [addressAutoFilledByZip, setAddressAutoFilledByZip] = useState<string | null>(null);

  // "instant" email availability check (best-effort)
  const [registerEmailHelper, setRegisterEmailHelper] = useState<string | null>(null);
  const [registerEmailChecking, setRegisterEmailChecking] = useState(false);
  const [registerEmailHasBlurred, setRegisterEmailHasBlurred] = useState(false);
  const [registerEmailAvailability, setRegisterEmailAvailability] = useState<'idle' | 'available' | 'taken' | 'invalid'>('idle');
  const emailAbortRef = useRef<AbortController | null>(null);
  const lastEmailCheckedRef = useRef<string>('');

  const formatAuthErrorJa = (err: any): string => {
    const msg = String(err?.message ?? '');
    if (msg.includes('User already registered')) {
      return 'そのメールアドレスは既に登録されています。ログインしてください。';
    }
    if (msg.includes('Invalid login credentials')) {
      return 'メールアドレスまたはパスワードが正しくありません。';
    }
    if (msg.includes('Email not confirmed')) {
      return 'メールアドレスの確認が完了していません。確認メールをご確認ください。';
    }
    return msg || '処理に失敗しました。しばらくしてから再度お試しください。';
  };

  const parseRetryAfterSeconds = (err: any): number | null => {
    const msg = String(err?.message ?? '');
    const match = msg.match(/after\s+(\d+)\s+seconds?/i);
    if (!match) return null;
    const seconds = Number(match[1]);
    return Number.isFinite(seconds) ? seconds : null;
  };

  const markAddressManuallyEdited = () => {
    setAddressAutoFilledByZip(null);
  };

  const lookupAddressByZipcode = async (zipcode: string) => {
    const zip = zipcode.trim();
    if (zip.length !== 7) return;
    if (zip === lastZipLookupRef.current) return;

    lastZipLookupRef.current = zip;
    zipAbortRef.current?.abort();
    const controller = new AbortController();
    zipAbortRef.current = controller;

    setZipLookupLoading(true);
    try {
      const resp = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${encodeURIComponent(zip)}`, {
        method: 'GET',
        signal: controller.signal,
      });
      if (!resp.ok) return;
      const json = await resp.json();
      const first = Array.isArray(json?.results) ? json.results[0] : null;
      if (!first) return;

      const nextPref = String(first.address1 ?? '').trim();
      const nextCity = String(first.address2 ?? '').trim();
      const nextTown = String(first.address3 ?? '').trim(); // 町名など（例: 丸の内）

      // Only override when fields are empty OR previously auto-filled by zipcode.
      const canOverride = addressAutoFilledByZip === null || addressAutoFilledByZip === zip;
      if (canOverride) {
        if (!registerPrefecture.trim()) setRegisterPrefecture(nextPref);
        if (!registerCity.trim()) setRegisterCity(nextCity);
        if (!registerTownStreet.trim()) setRegisterTownStreet(nextTown);
        if (!registerBuildingName.trim()) setRegisterBuildingName('');
        setAddressAutoFilledByZip(zip);
      }
    } catch (e: any) {
      // Ignore network errors (manual input still works)
    } finally {
      setZipLookupLoading(false);
    }
  };

  useEffect(() => {
    // cleanup in-flight request on unmount
    return () => zipAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    // Cleanup email check request on unmount
    return () => emailAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!resendAvailableAtMs) return;
    const timer = window.setInterval(() => {
      setResendNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendAvailableAtMs]);

  const resendRemainingSeconds = (() => {
    if (!resendAvailableAtMs) return 0;
    const ms = resendAvailableAtMs - resendNowMs;
    return ms > 0 ? Math.ceil(ms / 1000) : 0;
  })();
  const resendWaitMessage =
    resendRemainingSeconds > 0
      ? `確認メールの再送はあと約${resendRemainingSeconds}秒お待ちください。`
      : null;
  const hasResentConfirmationEmail = formSuccess === '確認メールを再送信しました。';

  useBodyBackgroundOnMount(AUTH_LIGHT_SCREEN_BG);

  const checkRegisterEmailAvailability = async (rawEmail: string): Promise<string | null> => {
    const email = rawEmail.trim().toLowerCase();
    if (!email) {
      setRegisterEmailHelper(null);
      setRegisterEmailChecking(false);
      setRegisterEmailAvailability('idle');
      return null;
    }

    const v = validateEmail(email);
    if (!v.valid) {
      const message = v.error ?? '有効なメールアドレスを入力してください';
      setRegisterEmailHelper(message);
      setRegisterEmailChecking(false);
      setRegisterEmailAvailability('invalid');
      return message;
    }

    if (email === lastEmailCheckedRef.current) {
      return registerEmailHelper;
    }
    lastEmailCheckedRef.current = email;

    emailAbortRef.current?.abort();
    const controller = new AbortController();
    emailAbortRef.current = controller;

    setRegisterEmailHelper(null);
    setRegisterEmailChecking(true);
    try {
      const url = `https://${projectId}.supabase.co/functions/v1/smart-worker/check-email?email=${encodeURIComponent(email)}`;
      const resp = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          apikey: publicAnonKey,
        },
      });
      if (!resp.ok) {
        const message = 'メールアドレスの確認に失敗しました。少し時間をおいて再度お試しください。';
        setRegisterEmailAvailability('invalid');
        setRegisterEmailHelper(message);
        return message;
      }
      const json = await resp.json();
      const message = json?.available === false ? 'このメールアドレスは登録済です。' : null;
      setRegisterEmailAvailability(message ? 'taken' : 'available');
      setRegisterEmailHelper(message);
      return message;
    } catch (e) {
      const message = 'メールアドレスの確認に失敗しました。少し時間をおいて再度お試しください。';
      setRegisterEmailAvailability('invalid');
      setRegisterEmailHelper(message);
      return message;
    } finally {
      setRegisterEmailChecking(false);
    }
  };

  const checkRegisterEmailOnBlur = async () => {
    // Only check in register mode, and only after user leaves the field (blur).
    if (isLogin) return;

    setRegisterEmailHasBlurred(true);
    await checkRegisterEmailAvailability(registerEmail);
  };

  useEffect(() => {
    // Reset wizard when switching modes
    if (isLogin) {
      setRegisterStage('auth');
      setPendingRegistrationEmail(null);
      setRegisterEmailHasBlurred(false);
      setRegisterEmailAvailability('idle');
      setRegisterPasswordHasBlurred(false);
      setRegisterConfirmPasswordHasBlurred(false);
    }
  }, [isLogin]);

  const handleResendConfirmationEmail = async () => {
    const email = pendingRegistrationEmail?.trim() || registerEmail.trim();
    if (!email) return;

    setFormError(null);
    setFormSuccess(null);
    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
        },
      });
      if (error) throw error;
      setResendAvailableAtMs(null);
      setFormSuccess('確認メールを再送信しました。');
    } catch (error: any) {
      const retryAfterSeconds = parseRetryAfterSeconds(error);
      if (retryAfterSeconds) {
        setResendAvailableAtMs(Date.now() + retryAfterSeconds * 1000);
        setFormError(null);
      } else {
        setFormError(formatAuthErrorJa(error) || '確認メールの再送に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const registerPasswordValidation = validatePassword(registerPassword);
  const registerPasswordFieldError =
    registerPasswordHasBlurred && !registerPasswordValidation.valid
      ? registerPasswordValidation.error ?? 'パスワードが無効です'
      : null;
  const registerPasswordFieldOk =
    registerPasswordHasBlurred &&
    !!registerPassword &&
    registerPasswordValidation.valid;
  const registerConfirmPasswordFieldError =
    registerConfirmPasswordHasBlurred &&
    !!registerConfirmPassword &&
    registerPassword !== registerConfirmPassword
      ? 'パスワードが一致しません'
      : null;
  const registerConfirmPasswordFieldOk =
    registerConfirmPasswordHasBlurred &&
    !!registerConfirmPassword &&
    registerPasswordValidation.valid &&
    registerPassword === registerConfirmPassword;
  const registerEmailFieldError =
    registerEmailHasBlurred && registerEmailHelper ? registerEmailHelper : null;

  const resolveLoginAccessForUser = async (
    userId: string,
    personalUser?: { user_metadata?: Record<string, unknown> } | null,
  ): Promise<
    | { kind: 'active'; clinicId: string }
    | { kind: 'device'; clinicId: string | null }
    | { kind: 'disabled_staff' }
    | { kind: 'none' }
  > => {
    const { data: cu, error: cuError } = await supabase
      .from('clinic_users')
      .select('clinic_id, user_kind, is_active')
      .eq('auth_user_id', userId)
      .limit(20);
    if (cuError) throw cuError;

    const rows = Array.isArray(cu) ? cu : [];
    const activeMembership = rows.find((row: any) => row.is_active);
    const hasLegacyDisabledStaffMembership = rows.some(
      (row: any) => row.user_kind === 'staff' && !row.is_active,
    );

    if (activeMembership) {
      const membershipKind = (activeMembership as any)?.user_kind as string | undefined;
      const clinicId = (activeMembership as any)?.clinic_id as string | undefined;
      if (membershipKind === 'device') {
        return { kind: 'device', clinicId: clinicId ?? null };
      }
      if (membershipKind === 'staff' && personalUser && !isPersonalLoginEnabled(personalUser as any)) {
        return { kind: 'disabled_staff' };
      }
      if (clinicId) {
        return { kind: 'active', clinicId };
      }
    }

    if (hasLegacyDisabledStaffMembership || (personalUser && !isPersonalLoginEnabled(personalUser as any))) {
      return { kind: 'disabled_staff' };
    }

    return { kind: 'none' };
  };

  const resetOnboardingFields = () => {
    setRegisterClinicName('');
    setRegisterZipcode('');
    setRegisterPrefecture('');
    setRegisterCity('');
    setRegisterTownStreet('');
    setRegisterBuildingName('');
    setRegisterClinicPhone('');
    setRegisterInitialStaffName('');
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        const user = data.session?.user;
        if (!user?.id) return;
        if (requiresPasswordSetup(user)) {
          navigate('/setup-password');
          return;
        }

        const access = await resolveLoginAccessForUser(user.id, user);
        if (cancelled) return;
        if (access.kind === 'device') {
          if (access.clinicId) {
            navigate(`/${access.clinicId}`);
            return;
          }
          await supabase.auth.signOut();
          setFormError('所属情報の取得に失敗しました。管理者に連絡してください。');
          return;
        }
        if (access.kind === 'disabled_staff') {
          await supabase.auth.signOut();
          setFormError('この個人ログインは停止されています。');
          return;
        }

        // If user already belongs to a clinic, jump in.
        if (access.kind === 'active') {
          navigate(`/${access.clinicId}`);
          return;
        }

        // Email confirmが完了してログイン済みだが、まだ医院未作成 → 初期登録（医院/ユーザー作成）へ。
        setIsLogin(false);
        resetOnboardingFields();
        setRegisterStage('clinic');
        setRegisterEmail(user.email ?? '');
        setRegisterEmailHasBlurred(false);
        setRegisterPassword('');
        setRegisterConfirmPassword('');
      } catch (e: any) {
        // keep the user on the current page
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    
    // バリデーション
    const emailValidation = validateEmail(loginEmail);
    if (!emailValidation.valid) {
      setFormError(emailValidation.error ?? '有効なメールアドレスを入力してください');
      return;
    }
    
    if (!loginPassword) {
      setFormError('パスワードを入力してください');
      return;
    }

    setLoading(true);
    try {
      await signInWithPassword(loginEmail, loginPassword);

      // After auth, resolve clinic from membership and jump into clinic app.
      const { data } = await supabase.auth.getUser();
      const userId = data.user?.id;
      if (userId) {
        const access = await resolveLoginAccessForUser(userId, data.user as any);
        if (access.kind === 'active') {
          navigate(`/${access.clinicId}`);
          return;
        }
        if (access.kind === 'disabled_staff') {
          await supabase.auth.signOut();
          setFormError('この個人ログインは停止されています。');
          return;
        }
        if (access.kind === 'device') {
          if (access.clinicId) {
            navigate(`/${access.clinicId}`);
            return;
          }
          await supabase.auth.signOut();
          setFormError('所属情報の取得に失敗しました。管理者に連絡してください。');
          return;
        }

        // Logged in but no membership yet → onboarding
        setIsLogin(false);
        resetOnboardingFields();
        setRegisterStage('clinic');
        setRegisterEmail(data.user?.email ?? loginEmail);
        setRegisterEmailHasBlurred(false);
        setRegisterPassword('');
        setRegisterConfirmPassword('');
        setFormSuccess('ログインしました。続けて初期登録を行ってください。');
        return;
      }

      navigate('/account');
    } catch (error: any) {
      console.error('Login error:', error);
      const raw = String(error?.message ?? '');
      if (raw.includes('infinite recursion detected in policy for relation')) {
        setFormError('権限設定（RLS）に不整合があります。管理者に連絡してください。');
        navigate('/account');
      } else {
        setFormError(formatAuthErrorJa(error) || 'ログインに失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    setRegisterEmailHasBlurred(true);
    setRegisterPasswordHasBlurred(true);
    setRegisterConfirmPasswordHasBlurred(true);

    const emailValidation = validateEmail(registerEmail);
    if (!emailValidation.valid) {
      setFormError(emailValidation.error ?? '有効なメールアドレスを入力してください');
      return;
    }
    const registerEmailAvailabilityError = await checkRegisterEmailAvailability(registerEmail);
    if (registerEmailAvailabilityError?.includes('登録済')) {
      setFormError(registerEmailAvailabilityError);
      return;
    }

    const passwordValidation = validatePassword(registerPassword);
    if (!passwordValidation.valid) {
      setFormError(passwordValidation.error ?? 'パスワードが無効です');
      return;
    }
    if (registerPassword !== registerConfirmPassword) {
      setFormError('パスワードが一致しません');
      return;
    }

    setLoading(true);
    try {
      // 1) Supabase Auth user creation (email + password)
      const signUpRes = await supabase.auth.signUp({
        email: registerEmail,
        password: registerPassword,
        options: {
          // Ensure the user returns to /login after clicking the confirm link.
          // IMPORTANT: This URL must be allow-listed in Supabase Auth settings.
          emailRedirectTo: `${window.location.origin}/login`,
        },
      });
      if (signUpRes.error) {
        throw signUpRes.error;
      }

      // If session is not returned (email confirmation required), stop here.
      const session1 = signUpRes.data.session;
      if (!session1) {
        setPendingRegistrationEmail(registerEmail.trim());
        setRegisterPassword('');
        setRegisterConfirmPassword('');
        setFormSuccess(null);
        setLoginPassword('');
        return;
      }

      // Confirm not required (dev settings) or already confirmed: continue to onboarding.
      resetOnboardingFields();
      setRegisterStage('clinic');
    } catch (error: any) {
      console.error('Registration error:', error);
      setFormError(formatAuthErrorJa(error) || '会員登録に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterClinicNext = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!registerClinicName.trim()) {
      setFormError('医院名を入力してください');
      return;
    }

    const zipcodeValidation = validateZipcode(registerZipcode);
    if (!zipcodeValidation.valid) {
      setFormError(zipcodeValidation.error ?? '郵便番号が無効です');
      return;
    }

    if (registerZipcode.trim().length === 7) {
      void lookupAddressByZipcode(registerZipcode.trim());
    }

    if (!registerPrefecture.trim()) {
      setFormError('都道府県を入力してください');
      return;
    }
    if (!registerCity.trim()) {
      setFormError('市区町村を入力してください');
      return;
    }
    if (!registerTownStreet.trim()) {
      setFormError('町名番地を入力してください');
      return;
    }
    const clinicPhoneDigits = registerClinicPhone.replace(/\D/g, '');
    if (!clinicPhoneDigits) {
      setFormError('医院の電話番号を入力してください');
      return;
    }

    setRegisterStage('profile');
  };

  const handleRegisterBootstrap = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!registerInitialStaffName.trim()) {
      setFormError('初期スタッフ名を入力してください');
      return;
    }
    if (!registerClinicName.trim()) {
      setFormError('医院名を入力してください');
      return;
    }

    const zipcodeValidation = validateZipcode(registerZipcode);
    if (!zipcodeValidation.valid) {
      setFormError(zipcodeValidation.error ?? '郵便番号が無効です');
      return;
    }
    if (!registerPrefecture.trim()) {
      setFormError('都道府県を入力してください');
      return;
    }
    if (!registerCity.trim()) {
      setFormError('市区町村を入力してください');
      return;
    }
    if (!registerTownStreet.trim()) {
      setFormError('町名番地を入力してください');
      return;
    }
    const clinicPhoneDigits = registerClinicPhone.replace(/\D/g, '');
    if (!clinicPhoneDigits) {
      setFormError('医院の電話番号を入力してください');
      return;
    }

    // 初期PINは医院電話番号の下4桁（サーバ側で0000回避・重複回避のフォールバックあり）
    const initialPin = clinicPhoneDigits.slice(-4);

    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        throw new Error('認証が必要です（メール確認後にログインしてください）');
      }

      const addressLine = `${registerTownStreet}${registerBuildingName.trim() ? ` ${registerBuildingName.trim()}` : ''}`;

      const bootstrapUrl = `https://${projectId}.supabase.co/functions/v1/smart-worker/bootstrap-clinic`;
      const resp = await fetch(bootstrapUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          clinicName: registerClinicName,
          zipcode: registerZipcode,
          prefecture: registerPrefecture,
          city: registerCity,
          addressLine,
          clinicPhone: clinicPhoneDigits,
          initialStaffName: registerInitialStaffName,
          initialStaffPhoneNumber: '',
          initialStaffPin: initialPin,
        }),
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || '初期登録に失敗しました');
      }
      const json = await resp.json();
      const clinicId = json?.clinicId as string | undefined;
      if (!clinicId) {
        throw new Error('初期登録に失敗しました（clinicIdがありません）');
      }

      const createdPin = (json?.initialStaff?.pin as string | undefined) ?? initialPin;
      setCompletedPin(createdPin);
      setCompletedClinicId(clinicId);
      setRegisterStage('done');
    } catch (error: any) {
      console.error('Bootstrap error:', error);
      setFormError(String(error?.message || '初期登録に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const isRegisterAuthReady = (): boolean => {
    const emailValidation = validateEmail(registerEmail);
    if (!emailValidation.valid) return false;
    if (registerEmailHelper?.includes('登録済')) return false;

    const passwordValidation = validatePassword(registerPassword);
    if (!passwordValidation.valid) return false;
    if (registerPassword !== registerConfirmPassword) return false;
    return true;
  };

  const isRegisterClinicReady = (): boolean => {
    if (!registerClinicName.trim()) return false;
    const zipcodeValidation = validateZipcode(registerZipcode);
    if (!zipcodeValidation.valid) return false;
    if (!registerPrefecture.trim()) return false;
    if (!registerCity.trim()) return false;
    if (!registerTownStreet.trim()) return false;
    if (!registerClinicPhone.replace(/\D/g, '').trim()) return false;
    return true;
  };

  const isRegisterProfileReady = (): boolean => {
    if (!isRegisterClinicReady()) return false;
    if (!registerInitialStaffName.trim()) return false;
    return true;
  };

  return (
    <div
      className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-gradient-to-br from-teal-50 to-blue-50 px-4"
      style={{
        paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div className="flex flex-1 flex-col items-center justify-center py-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-teal-600">
            Handra
          </CardTitle>
          <CardDescription>
            {isLogin ? '会員ログイン' : '会員登録'}
          </CardDescription>
          {!isLogin && registerStage === 'clinic' && (
            <div className="mt-2 text-sm text-gray-600">
              医院情報の登録を行います。すべて必須となっております。
            </div>
          )}
          {!isLogin && registerStage === 'profile' && (
            <div className="mt-2 space-y-1 text-sm text-gray-600">
              <div>最初のスタッフ登録を行います。</div>
              <div>
                今操作している方を初期スタッフ（管理者）として登録してください。管理者はあとから変更できます。
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isLogin ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">メールアドレス</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="text"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="example@email.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="pl-10 placeholder:text-gray-300"
                    required
                    disabled={loading}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">パスワード</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type={showLoginPassword ? 'text' : 'password'}
                    placeholder="パスワードを入力"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="pl-10 pr-10 placeholder:text-gray-300"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    aria-label={showLoginPassword ? 'パスワードを非表示にする' : 'パスワードを表示する'}
                    className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center text-gray-500 hover:text-gray-700"
                    onClick={() => setShowLoginPassword(v => !v)}
                    disabled={loading}
                  >
                    {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              {formError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {formError}
                </p>
              )}
              {formSuccess && (
                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                  {formSuccess}
                </p>
              )}

              <div className="pt-6">
                <Button type="submit" variant="cta" className="w-full" disabled={loading}>
                  {loading ? 'ログイン中...' : 'ログイン'}
                </Button>
              </div>
              
              <div className="text-center text-sm space-y-2">
                <div>
                  <button
                    type="button"
                    onClick={() => setIsLogin(false)}
                    className="text-teal-600 hover:underline"
                  >
                    会員登録はこちら
                  </button>
                </div>
                <div>
                  <Link to="/forgot-password" className="text-teal-600 hover:underline">
                    パスワードを忘れた方
                  </Link>
                </div>
              </div>
            </form>
          ) : (
            <>
              {registerStage === 'auth' ? (
                pendingRegistrationEmail ? (
                  <div className="space-y-4">
                    <div className="space-y-2 text-sm text-gray-900">
                      <p>
                        {hasResentConfirmationEmail ? (
                          <>
                            確認メールを <span className="font-semibold">再送信</span> しました。
                          </>
                        ) : (
                          '確認メールを送信しました。'
                        )}
                      </p>
                      <p className="break-all">{pendingRegistrationEmail}</p>
                      <p>メール内のリンクを開いてから続けてください。</p>
                    </div>

                    {formError && (
                      <p className="text-sm font-medium text-red-600">
                        {formError}
                      </p>
                    )}
                    {resendWaitMessage && (
                      <p className="text-sm font-medium text-red-600">
                        {resendWaitMessage}
                      </p>
                    )}
                    {formSuccess && !hasResentConfirmationEmail && (
                      <p className="text-sm text-gray-900">
                        {formSuccess}
                      </p>
                    )}

                    <div className="space-y-3 pt-2">
                      <Button
                        type="button"
                        variant="cta"
                        className="w-full"
                        disabled={loading || resendRemainingSeconds > 0}
                        onClick={handleResendConfirmationEmail}
                      >
                        {loading ? '再送中...' : '確認メールを再送する'}
                      </Button>
                      <button
                        type="button"
                        onClick={() => {
                          setPendingRegistrationEmail(null);
                          setRegisterEmail('');
                          setRegisterEmailHelper(null);
                          setRegisterEmailHasBlurred(false);
                          setRegisterEmailAvailability('idle');
                          setRegisterPassword('');
                          setRegisterConfirmPassword('');
                          setRegisterPasswordHasBlurred(false);
                          setRegisterConfirmPasswordHasBlurred(false);
                          setFormError(null);
                          setFormSuccess(null);
                          setResendAvailableAtMs(null);
                        }}
                        className="w-full text-center text-sm text-teal-600 hover:underline"
                        disabled={loading}
                      >
                        別のメールアドレスで登録し直す
                      </button>
                    </div>
                  </div>
                ) : (
                <form onSubmit={handleRegisterAuth} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-email">医院の代表メールアドレス</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="register-email"
                        type="text"
                        inputMode="email"
                        autoComplete="email"
                        placeholder="example@email.com"
                        value={registerEmail}
                        onChange={(e) => {
                          // Don't show "invalid" while typing; validate on blur.
                          setRegisterEmail(e.target.value);
                          setRegisterEmailHasBlurred(false);
                          setRegisterEmailHelper(null);
                          setRegisterEmailChecking(false);
                          setRegisterEmailAvailability('idle');
                        }}
                        onBlur={checkRegisterEmailOnBlur}
                        className={
                          `pl-10 placeholder:text-gray-300 ${
                            registerEmailFieldError
                              ? 'bg-red-50 border-red-300 focus-visible:border-red-400 focus-visible:ring-red-300/50 focus-visible:ring-[3px]'
                              : ''
                          }`
                        }
                        required
                        disabled={loading}
                      />
                    </div>
                    {(registerEmailChecking || registerEmailFieldError || (registerEmailHasBlurred && registerEmailAvailability === 'available')) && (
                      <div
                        className={
                          `flex items-center gap-1 text-xs ${
                            registerEmailFieldError
                              ? 'font-bold text-red-600'
                              : 'font-medium text-green-600'
                          }`
                        }
                      >
                        {registerEmailChecking ? (
                          '確認中...'
                        ) : registerEmailFieldError ? (
                          registerEmailFieldError
                        ) : (
                          <>
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            <span>このメールアドレスは使用できます</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-password">{`パスワード（${MEMBER_PASSWORD_RULE_LABEL}）`}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="register-password"
                        type={showRegisterPassword ? 'text' : 'password'}
                        placeholder="パスワードを入力"
                        value={registerPassword}
                        onChange={(e) => setRegisterPassword(e.target.value)}
                        onBlur={() => setRegisterPasswordHasBlurred(true)}
                        className={`pl-10 pr-10 placeholder:text-gray-300 ${
                          registerPasswordFieldError
                            ? 'bg-red-50 border-red-300 focus-visible:border-red-400 focus-visible:ring-red-300/50 focus-visible:ring-[3px]'
                            : ''
                        }`}
                        required
                        disabled={loading}
                      />
                      <button
                        type="button"
                        aria-label={showRegisterPassword ? 'パスワードを非表示にする' : 'パスワードを表示する'}
                        className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center text-gray-500 hover:text-gray-700"
                        onClick={() => setShowRegisterPassword(v => !v)}
                        disabled={loading}
                      >
                        {showRegisterPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {registerPasswordFieldError && (
                      <p className="text-xs font-bold text-red-600">
                        {registerPasswordFieldError}
                      </p>
                    )}
                    {registerPasswordFieldOk && (
                      <div className="flex items-center gap-1 text-xs font-medium text-green-600">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        <span>このパスワードは使用できます</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-confirm-password">パスワード（確認）</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="register-confirm-password"
                        type={showRegisterConfirmPassword ? 'text' : 'password'}
                        placeholder="パスワードを再入力"
                        value={registerConfirmPassword}
                        onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                        onBlur={() => setRegisterConfirmPasswordHasBlurred(true)}
                        className={`pl-10 pr-10 placeholder:text-gray-300 ${
                          registerConfirmPasswordFieldError
                            ? 'bg-red-50 border-red-300 focus-visible:border-red-400 focus-visible:ring-red-300/50 focus-visible:ring-[3px]'
                            : ''
                        }`}
                        required
                        disabled={loading}
                      />
                      <button
                        type="button"
                        aria-label={showRegisterConfirmPassword ? 'パスワードを非表示にする' : 'パスワードを表示する'}
                        className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center text-gray-500 hover:text-gray-700"
                        onClick={() => setShowRegisterConfirmPassword(v => !v)}
                        disabled={loading}
                      >
                        {showRegisterConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {registerConfirmPasswordFieldError && (
                      <p className="text-xs font-bold text-red-600">
                        {registerConfirmPasswordFieldError}
                      </p>
                    )}
                    {registerConfirmPasswordFieldOk && (
                      <div className="flex items-center gap-1 text-xs font-medium text-green-600">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        <span>パスワードが一致しています</span>
                      </div>
                    )}
                  </div>

                  {formError && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                      {formError}
                    </p>
                  )}
                  {formSuccess && (
                    <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                      {formSuccess}
                    </p>
                  )}

                  <div className="pt-6">
                    <Button
                      type="submit"
                      variant="cta"
                      className="w-full"
                      disabled={loading || !isRegisterAuthReady()}
                    >
                      {loading ? '送信中...' : '確認メールを送信'}
                    </Button>
                  </div>

                  <div className="text-center text-sm">
                    <button
                      type="button"
                      onClick={() => setIsLogin(true)}
                      className="text-teal-600 hover:underline"
                    >
                      ログイン画面に戻る
                    </button>
                  </div>
                </form>
                )
              ) : registerStage === 'clinic' ? (
                <form onSubmit={handleRegisterClinicNext} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="clinic-name">医院名</Label>
                    <Input
                      id="clinic-name"
                      type="text"
                      placeholder="医院名を入力"
                      value={registerClinicName}
                      onChange={(e) => setRegisterClinicName(e.target.value)}
                      className="placeholder:text-gray-300"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="zipcode">郵便番号（ハイフンなし）</Label>
                    <Input
                      id="zipcode"
                      type="text"
                      placeholder="1234567"
                      value={registerZipcode}
                      onChange={(e) => {
                        const next = e.target.value.replace(/[^0-9]/g, '');
                        setRegisterZipcode(next);
                        if (next.length === 7) {
                          lookupAddressByZipcode(next);
                        }
                      }}
                      maxLength={7}
                      className="placeholder:text-gray-300"
                      required
                      disabled={loading}
                    />
                    {zipLookupLoading && (
                      <div className="text-xs text-gray-500">住所を取得中...</div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="prefecture">都道府県</Label>
                    <Input
                      id="prefecture"
                      type="text"
                      placeholder="例）東京都"
                      value={registerPrefecture}
                      onChange={(e) => {
                        markAddressManuallyEdited();
                        setRegisterPrefecture(e.target.value);
                      }}
                      className="placeholder:text-gray-300"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">市区町村</Label>
                    <Input
                      id="city"
                      type="text"
                      placeholder="例）〇〇市 / 〇〇区"
                      value={registerCity}
                      onChange={(e) => {
                        markAddressManuallyEdited();
                        setRegisterCity(e.target.value);
                      }}
                      className="placeholder:text-gray-300"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="town-street">町名番地</Label>
                    <Input
                      id="town-street"
                      type="text"
                      placeholder="例）丸の内1-1-1"
                      value={registerTownStreet}
                      onChange={(e) => {
                        markAddressManuallyEdited();
                        setRegisterTownStreet(e.target.value);
                      }}
                      className="placeholder:text-gray-300"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="building-name">建物名（任意）</Label>
                    <Input
                      id="building-name"
                      type="text"
                      placeholder="例）〇〇ビル10F"
                      value={registerBuildingName}
                      onChange={(e) => {
                        markAddressManuallyEdited();
                        setRegisterBuildingName(e.target.value);
                      }}
                      className="placeholder:text-gray-300"
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="clinic-phone">医院の電話番号（ハイフンなし）</Label>
                    <Input
                      id="clinic-phone"
                      type="tel"
                      placeholder="0312345678"
                      value={registerClinicPhone}
                      onChange={(e) => setRegisterClinicPhone(e.target.value.replace(/\D/g, ''))}
                      className="placeholder:text-gray-300"
                      required
                      disabled={loading}
                    />
                  </div>

                  {formError && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                      {formError}
                    </p>
                  )}

                  <div className="pt-6 space-y-3">
                    <Button
                      type="submit"
                      variant="cta"
                      className="w-full"
                      disabled={loading || !isRegisterClinicReady()}
                    >
                      {loading ? '処理中...' : '次へ'}
                    </Button>

                    <button
                      type="button"
                      className="w-full text-sm text-gray-500 hover:underline"
                      disabled={loading}
                      onClick={() => setRegisterStage('auth')}
                    >
                      戻る
                    </button>
                  </div>
                </form>
              ) : registerStage === 'profile' ? (
                <form onSubmit={handleRegisterBootstrap} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-initial-staff-name">初期スタッフ名（管理者）</Label>
                    <Input
                      id="register-initial-staff-name"
                      type="text"
                      placeholder="例）田中太郎"
                      value={registerInitialStaffName}
                      onChange={(e) => setRegisterInitialStaffName(e.target.value)}
                      className="placeholder:text-gray-300"
                      required
                      disabled={loading}
                    />
                  </div>

                  {formError && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                      {formError}
                    </p>
                  )}

                  <div className="pt-6 space-y-3">
                    <Button
                      type="submit"
                      variant="cta"
                      className="w-full"
                      disabled={loading || !isRegisterProfileReady()}
                    >
                      {loading ? '登録中...' : '初期登録を完了'}
                    </Button>

                    <button
                      type="button"
                      className="w-full text-sm text-gray-500 hover:underline"
                      disabled={loading}
                      onClick={() => setRegisterStage('clinic')}
                    >
                      戻る
                    </button>
                  </div>
                </form>
              ) : (
                /* registerStage === 'done' */
                <div className="space-y-6">
                  <div className="text-center space-y-2">
                    <p className="text-sm text-gray-700">初期登録が完了しました。</p>
                  </div>

                  <div className="space-y-2">
                    <Label>スタッフ名</Label>
                    <p className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-800">
                      {registerInitialStaffName}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>初期PIN（ログイン時に使用）</Label>
                    <p className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-2xl font-mono font-bold text-center tracking-widest text-teal-700">
                      {completedPin}
                    </p>
                    <p className="text-xs text-gray-500">
                      このPINはこの画面でのみ表示されます。必要に応じて控えてください。PINはあとから変更できます。
                    </p>
                  </div>

                  <div className="pt-4">
                    <Button type="button" variant="cta" className="w-full" onClick={() => navigate(`/${completedClinicId}`)}>
                      はじめる
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}

